import React, { useState } from 'react';
import { useContent } from '../i18n/LanguageContext';
import { Download, ArrowRight, BatteryCharging } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { BROCHURE_URL } from '../i18n/media';

export default function Product({ onDemo }) {
  const c = useContent();
  const p = c.product;
  const uc = c.useCases;
  const dl = p.detailLabels;
  const [selected, setSelected] = useState(null);
  const item = selected !== null ? p.range[selected] : null;
  return (
    <section id="tag" className="py-14 md:py-20 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 grid-dots opacity-[0.06]" />
      <div className="lt-container relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative reveal">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-teal-500/20 to-cyan-500/10 blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl bg-gradient-to-b from-white to-slate-100">
              <img src={p.image} alt={p.title} className="w-full h-[420px] object-contain p-10" />
            </div>
            <div className="absolute -bottom-5 left-6 right-6 glass !bg-white/95 rounded-2xl p-4 text-slate-900 flex items-center justify-between shadow-xl">
              <div>
                <p className="text-xs text-slate-500">{p.chipsetLabel}</p>
                <p className="font-display font-bold">K12 · {p.chipset}</p>
              </div>
              <span className="text-xs font-semibold text-teal-700 bg-teal-100 px-3 py-1.5 rounded-full">IP67</span>
            </div>
          </div>

          <div className="reveal">
            <p className="text-sm font-semibold uppercase tracking-widest text-teal-400">{p.eyebrow}</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold leading-tight">{p.title}</h2>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">{p.subtitle}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {p.specs.map((s) => (
                <div key={s.title} className="rounded-2xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-teal-500/15 text-teal-300">
                    <s.icon className="w-5 h-5" />
                  </span>
                  <h4 className="mt-3 font-semibold text-sm">{s.title}</h4>
                  <p className="mt-1 text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={onDemo} className="btn-shine h-12 px-6 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-full">
                {c.cta.demo}<ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href={BROCHURE_URL} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:bg-white/10 transition-colors text-sm font-semibold">
                <Download className="w-4 h-4" /> {c.cta.brochure}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {p.advantages.map((a, i) => (
            <div key={a.title} className="reveal rounded-2xl bg-white/5 border border-white/10 p-6" style={{ animationDelay: `${i * 70}ms` }}>
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-500/15 text-teal-300">
                <a.icon className="w-6 h-6" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold">{a.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
        {/* Tag models */}
        <div className="mt-16">
          <div className="flex flex-col items-center text-center reveal">
            <h3 className="font-display text-2xl md:text-3xl font-bold">{p.modelsTitle}</h3>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-500/15 border border-teal-400/30 text-teal-300 px-4 py-2 text-sm font-semibold">
              <BatteryCharging className="w-4 h-4" /> {p.autonomyBadge}
            </span>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {p.models.map((m, i) => (
              <div key={m.name} className={`reveal group relative rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${m.highlight ? 'border-teal-400 bg-white/[0.07] ring-1 ring-teal-400/40' : 'border-white/10 bg-white/5 hover:bg-white/10'}`} style={{ animationDelay: `${i * 60}ms` }}>
                {m.highlight && (
                  <span className="absolute top-3 left-3 z-10 rounded-full bg-teal-500 text-slate-900 text-[10px] font-bold px-2.5 py-0.5">{p.flagship}</span>
                )}
                <div className="relative h-36 bg-gradient-to-b from-white to-slate-100 grid place-items-center">
                  <img src={m.img} alt={m.name} className="h-28 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
                  <span className="absolute top-3 right-3 text-[10px] font-bold text-teal-700 bg-teal-100 rounded-full px-2 py-0.5">{m.ip}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-baseline justify-between">
                    <h4 className="font-display text-xl font-bold tracking-tight text-white">{m.name}</h4>
                    <span className="text-xs text-teal-300 font-semibold">{p.upTo} {m.years} {p.yearsUnit}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.modelUses[i]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Range of formats */}
        <div className="mt-16">
          <h3 className="font-display text-2xl md:text-3xl font-bold text-center reveal">{p.rangeTitle}</h3>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {p.range.map((r, i) => (
              <button key={r.title} onClick={() => setSelected(i)} className="reveal text-left rounded-2xl bg-white/5 border border-white/10 p-4 text-center hover:bg-white/10 hover:border-teal-400/40 transition-colors group" style={{ animationDelay: `${i * 60}ms` }}>
                <span className="mx-auto grid place-items-center w-11 h-11 rounded-xl bg-teal-500/15 text-teal-300 group-hover:bg-teal-500 group-hover:text-slate-900 transition-colors">
                  <r.icon className="w-5 h-5" />
                </span>
                <h4 className="mt-3 font-semibold text-sm text-center">{r.title}</h4>
                <p className="mt-1 text-xs text-slate-400 leading-relaxed text-center">{r.spec}</p>
                <span className="mt-2 inline-block text-[11px] font-semibold text-teal-300 opacity-0 group-hover:opacity-100 transition-opacity">{dl.more} →</span>
              </button>
            ))}
          </div>
        </div>

        {/* Use cases */}
        <div className="mt-16">
          <div className="max-w-3xl reveal">
            <p className="text-sm font-semibold uppercase tracking-widest text-teal-400">{uc.eyebrow}</p>
            <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold">{uc.title}</h3>
            <p className="mt-3 text-slate-300 text-lg">{uc.subtitle}</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {uc.items.map((it, i) => (
              <div key={it.title} className="reveal group rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-teal-400/30 transition-colors" style={{ animationDelay: `${(i % 3) * 70}ms` }}>
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-600/25">
                  <it.icon className="w-6 h-6" />
                </span>
                <h4 className="mt-4 font-display text-base font-semibold">{it.title}</h4>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={selected !== null} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg bg-white text-slate-900">
          {item && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-teal-50 text-teal-600"><item.icon className="w-5 h-5" /></span>
                  {item.title}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 -mt-1">{item.spec}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  [dl.dimensions, item.tech.dims],
                  [dl.weight, item.tech.weight],
                  [dl.battery, item.battery],
                  [dl.range, item.tech.range],
                  [dl.protection, item.tech.ip],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
                    <p className="mt-0.5 font-display font-semibold text-slate-900">{val}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{dl.features}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {item.features.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{f}
                    </span>
                  ))}
                </div>
              </div>
              <Button onClick={() => { setSelected(null); onDemo && onDemo(); }} className="btn-shine mt-6 w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold">
                {c.cta.demo}<ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
